/**
 * "Add a document to this release" — the installation guide, the management
 * guide, whatever ships with a version besides its change log and disks.
 *
 * IT BELONGS TO THE RELEASE, NOT TO THE DOCUMENTATION LIBRARY (CEO, 2026-09-16).
 * The dialog used to ask which Documentation FOLDER to file it in, which put
 * release material into the library tree where it read as general reading.
 * It no longer asks and no longer sends a `document_category_id`: the upload
 * is pinned to this release (`default_release_id`) and listed on the version,
 * for whoever its audience allows, and nowhere else.
 *
 * The record is still an ordinary Document — one model, one resolver, one
 * audited upload path — because a fourth artifact kind would mean a second
 * access rule to keep in step. What changed is where it is SHOWN.
 *
 * THE AUDIENCE IS THE POINT. A customer who bought only Mass Notification must
 * get the Mass Notification guide and not the whole product's library, so
 * the dialog asks who the document is for and defaults to "customers on this
 * release" — the narrowest answer that is always true for a release
 * document. "Customers who own a feature" is the per-feature answer. The
 * decision itself is made once, in portal-api's `Documents::AccessResolver`;
 * this dialog only records the intent.
 *
 * Uploads go through the SAME multipart endpoint the Documentation section's upload
 * page uses (`uploadDoc` → `/api/documentation/documents/upload`), so the file
 * lands in the same bucket and obeys the same validator (document types, no
 * size cap since 2026-10-31).
 * Nothing here is release-specific except the pin — and the absent folder.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DocAccessScope, DocFeature } from "@xop/dashboard-shared";
import { Upload } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";

import { MultiSelect } from "../../../components/ui/multi-select.js";
import { listDocFeatures, listDocProducts, uploadDoc } from "../../../pages/documentation/api.js";

interface Props {
  releaseId: number;
  version: string;
  /** The release's product code, when known — preselects the product for a
   * product-scoped guide and narrows the feature list to that product. */
  productName?: string | null;
  open: boolean;
  onClose: () => void;
  onUploaded?: () => void;
}

/** The audiences a RELEASE document can have. `private` is deliberately
 *  absent — a guide that ships with a version is never one customer's. */
type ReleaseDocScope = Exclude<DocAccessScope, "private">;

const SCOPE_HELP: Record<ReleaseDocScope, string> = {
  release: "Customers running this version, and every reseller of its product.",
  feature: "Only customers with one of these features switched on — e.g. Mass Notification — and resellers of its product.",
  product: "Every customer who owns the product, whatever version they run.",
  general: "Everyone who can open the Documentation section, customers and resellers alike.",
};

export function featureLabel(f: DocFeature): string {
  return f.product_name ? `${f.product_name} · ${f.name}` : f.name;
}

export function ReleaseDocumentUploadDialog({
  releaseId,
  version,
  productName,
  open,
  onClose,
  onUploaded,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState<ReleaseDocScope>("release");
  const [featureCodes, setFeatureCodes] = useState<string[]>([]);
  const [productCodes, setProductCodes] = useState<string[]>([]);
  const [publish, setPublish] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const featuresQuery = useQuery({ queryKey: ["docs", "features"], queryFn: listDocFeatures, enabled: open });
  const productsQuery = useQuery({ queryKey: ["docs", "products"], queryFn: listDocProducts, enabled: open });

  const allFeatures = featuresQuery.data?.data ?? [];
  const products = productsQuery.data?.data ?? [];
  // Features of THIS release's product first; the rest still reachable.
  const features = productName
    ? [...allFeatures].sort((a, b) => Number(b.product_name === productName) - Number(a.product_name === productName))
    : allFeatures;

  // Reset per opening so a second upload never inherits the first's file.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setTitle("");
    setScope("release");
    setFeatureCodes([]);
    setProductCodes([]);
    setPublish(true);
    setError(null);
  }, [open]);

  // Switching to "customers who own the product" preselects THIS release's
  // product once the catalog has landed — the obvious answer, still editable.
  const switchScope = (next: ReleaseDocScope): void => {
    setScope(next);
    if (next === "product" && productCodes.length === 0 && productName) {
      setProductCodes(products.filter((p) => p.name === productName).map((p) => p.code));
    }
  };

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Pick a file to upload.");
      if (scope === "feature" && featureCodes.length === 0) throw new Error("Pick at least one feature.");
      if (scope === "product" && productCodes.length === 0) throw new Error("Pick at least one product.");
      return uploadDoc({
        file,
        ...(title.trim() ? { title: title.trim() } : {}),
        status: publish ? "published" : "draft",
        // No folder, deliberately: a release document is attached to the
        // version, not filed in the documentation library.
        default_release_id: String(releaseId),
        doc_type: "guide",
        access_scope: scope,
        // Only the mapping the chosen audience reads; the others go empty so
        // nothing stale gates the document.
        release_ids: scope === "release" ? [releaseId] : [],
        feature_codes: scope === "feature" ? featureCodes : [],
        product_codes: scope === "product" ? productCodes : [],
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "release-documents", releaseId] });
      void qc.invalidateQueries({ queryKey: ["docs"] });
      onUploaded?.();
      onClose();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Upload failed."),
  });

  const canSubmit = Boolean(file) && !uploadMut.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a document to release {version}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
          An installation guide, a management guide, a datasheet — anything that ships with this
          version. It stays attached to {version} and appears on this release for whoever the
          audience allows. It is not filed in the Documentation library.
        </DialogContentText>

        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<Upload size={14} />}
              onClick={() => fileInput.current?.click()}
            >
              {file ? "Change file" : "Choose file"}
            </Button>
            <input
              ref={fileInput}
              type="file"
              hidden
              data-testid="release-doc-file"
              aria-label="Release document file"
              accept=".pdf,.md,.txt,.doc,.docx,.rtf"
              onChange={(e) => {
                const picked = e.target.files?.[0] ?? null;
                setFile(picked);
                if (picked && !title.trim()) setTitle(picked.name.replace(/\.[^.]+$/, ""));
                e.target.value = "";
              }}
            />
            <Typography sx={{ fontSize: 13, color: file ? "text.primary" : "text.disabled" }}>
              {file ? file.name : "PDF, Word, Markdown or text — any size"}
            </Typography>
          </Box>

          <TextField
            size="small"
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`e.g. USN ${version} Installation Guide`}
          />

          <TextField
            select
            size="small"
            label="Who is it for"
            fullWidth
            value={scope}
            onChange={(e) => switchScope(e.target.value as ReleaseDocScope)}
            helperText={SCOPE_HELP[scope]}
            inputProps={{ "data-testid": "release-doc-scope" }}
          >
            <MenuItem value="release">Customers on this release</MenuItem>
            <MenuItem value="feature">Customers who own a feature</MenuItem>
            <MenuItem value="product">Customers who own the product</MenuItem>
            <MenuItem value="general">Everyone</MenuItem>
          </TextField>

          {scope === "feature" && (
            <Box>
              <Typography variant="caption" color="text.secondary" component="label">
                Features
              </Typography>
              <MultiSelect
                options={features.map((f) => ({ value: f.slug, label: `${featureLabel(f)} · ${f.slug}` }))}
                selected={featureCodes}
                onChange={setFeatureCodes}
                placeholder={featuresQuery.isLoading ? "Loading features…" : "Pick one or more features"}
                emptyMessage="No features have synced from the CRM yet."
              />
            </Box>
          )}

          {scope === "product" && (
            <Box>
              <Typography variant="caption" color="text.secondary" component="label">
                Products
              </Typography>
              <MultiSelect
                options={products.map((p) => ({ value: p.code, label: `${p.name} · ${p.code}` }))}
                selected={productCodes}
                onChange={setProductCodes}
                placeholder={productsQuery.isLoading ? "Loading products…" : "Pick one or more products"}
                emptyMessage="No products have synced from the CRM yet."
              />
            </Box>
          )}

          <FormControlLabel
            control={<Switch checked={publish} onChange={(e) => setPublish(e.target.checked)} />}
            label={publish ? "Publish now" : "Save as draft (staff only)"}
          />

          {error && (
            <Alert severity="error" sx={{ fontSize: 13 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => uploadMut.mutate()}>
          {uploadMut.isPending ? "Uploading…" : "Add document"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
